import { USER_PLAN_QUERY_KEY } from "@/components/Root/hooks/useUserPlanData";
import { TUserPlanData } from "@/types/plan-data";
import { TProductRefId } from "@/types/plan";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useAuth } from "reactfire";
import { DAYS_PER_MONTH } from "../../constants.checkout";
import { CHECKOUT_EVENTS } from "../../events.checkout";
import { handleResumePlan } from "../../handlers/handleResumePlan";
import { CheckoutService } from "../../service.checkout";

export function useResumePlanMutation() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (resumingPlanIds: TProductRefId[]) => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("No authentication token");

      const result = await handleResumePlan({
        token,
        resumingPlanIds,
      });

      if (!result.success) {
        throw new Error("Failed to resume plan");
      }

      return result;
    },
    onSuccess: (_result, resumingPlanIds) => {
      const planData = queryClient.getQueryData<TUserPlanData>([
        USER_PLAN_QUERY_KEY,
      ]);
      const daysPaused = planData?.pause_info?.days_paused;
      const pauseMonths =
        typeof daysPaused === "number"
          ? Math.round(daysPaused / DAYS_PER_MONTH)
          : undefined;

      CheckoutService.track(CHECKOUT_EVENTS.RESUME_PLAN_SUCCESS, {
        subscription_id: planData?.subscription_id,
        resumed_plan_ids: resumingPlanIds,
        days_paused: daysPaused,
        pause_months: pauseMonths,
      });

      // Refetch user plan data to update the UI
      queryClient.invalidateQueries({ queryKey: [USER_PLAN_QUERY_KEY] });
      toast.success("Plan resumed successfully");
    },
    onError: (error) => {
      CheckoutService.track(CHECKOUT_EVENTS.RESUME_PLAN_FAILED, {
        error: error instanceof Error ? error.message : "Unknown error",
      });
      console.error("Error resuming plan:", error);
      toast.error("Failed to resume plan. Please try again.");
    },
  });
}
